import React from "react";
import { CCol, CFormGroup, CLabel, CRow, CSelect } from "@coreui/react";
import { useAutoInvoiceFormContext } from "../context/AutoInvoiceFormContext";
import { FieldErrors } from "../../form/FieldErrors";
import { getFieldErrors } from "../../../models/errors";
import { CURRENCY_CHOICES, PYG } from "../../../currency/available-currencies";
import PYGField from "../../currencies/PYGField";
import CurrencyField from "../../currencies/CurrencyField";
import AnimatedCheckbox from "../../checkbox/AnimatedCheckbox";

const SectionDivider: React.FC<{ icon: string; label: string }> = ({ icon, label }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: "16px", marginTop: "16px" }}>
    <div style={{ width: "4px", height: "18px", background: "#006657", borderRadius: "2px", marginRight: "10px", flexShrink: 0 }} />
    <i className={`fa ${icon} mr-2`} style={{ fontSize: "0.85rem", color: "var(--theme-accent)" }}></i>
    <h6 style={{ margin: 0, fontWeight: 600, color: "var(--theme-muted)", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.8px" }}>
      {label}
    </h6>
    <div style={{ flex: 1, height: "1px", background: "var(--theme-divider)", marginLeft: "10px" }} />
  </div>
);

const FormLabel: React.FC<{ required?: boolean; children: React.ReactNode }> = ({ required, children }) => (
  <CLabel className="mb-0" style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--theme-text-soft)" }}>
    {required && <span className="text-danger mr-1">*</span>}
    {children}
  </CLabel>
);

const AutoInvoiceFormCurrencySection: React.FC = () => {
  const {
    autoInvoiceRef,
    currency,
    pygExchangeRate,
    totalAmount,
    applyDiscount,
    globalDiscount,
    errors,
    onCurrencyChange,
    onPygExchangeRateChange,
    onApplyDiscountChange,
    onGlobalDiscountChange,
  } = useAutoInvoiceFormContext();

  return (
    <>
      <SectionDivider icon="fa-calculator" label="Moneda y Totales" />
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel>Moneda:</FormLabel>
          </CCol>
          <CCol md={2}>
            <CSelect
              type="text"
              defaultValue={
                autoInvoiceRef.current !== null
                  ? autoInvoiceRef.current.currency
                  : PYG
              }
              onChange={onCurrencyChange}
            >
              <option value={""} disabled>
                -----
              </option>
              {Array.from(CURRENCY_CHOICES.entries()).map((entry) => {
                return (
                  <option key={entry[0]} value={entry[0]}>
                    {entry[1]}
                  </option>
                );
              })}
            </CSelect>
            <FieldErrors
              errors={getFieldErrors("currency", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
      {currency !== PYG ? (
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={2}>
              <FormLabel required>Cambio:</FormLabel>
            </CCol>
            <CCol md={2}>
              <PYGField
                placeholder="Total"
                limit={1000000000000}
                onChange={onPygExchangeRateChange}
                value={pygExchangeRate}
              />
              <FieldErrors
                errors={getFieldErrors("pygExchangeRate", errors) as string[]}
              />
            </CCol>
          </CRow>
        </CFormGroup>
      ) : (
        <></>
      )}
      <div
        style={{
          background: "var(--theme-highlight)",
          border: "1px solid var(--theme-highlight-border)",
          borderRadius: "8px",
          padding: "16px 20px",
          marginBottom: "16px",
        }}
      >
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={2}>
              <FormLabel required>Total:</FormLabel>
            </CCol>
            <CCol md={2}>
              <div style={{ fontWeight: 700, fontSize: "1.1rem", color: "var(--theme-accent)" }}>
                <CurrencyField
                  currency={currency}
                  placeholder="Total"
                  limit={1000000000000}
                  disabled={true}
                  value={totalAmount}
                />
              </div>
              <FieldErrors
                errors={getFieldErrors("amount", errors) as string[]}
              />
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup className="mb-0">
          <CRow className="align-items-center">
            <CCol md={2}>
              <FormLabel>Aplicar Descuento:</FormLabel>
            </CCol>
            <CCol md={2}>
              <CRow>
                <AnimatedCheckbox
                  value={
                    autoInvoiceRef.current?.globalDiscount !== undefined &&
                    autoInvoiceRef.current?.globalDiscount !== null
                  }
                  onChange={onApplyDiscountChange}
                />
              </CRow>
              <FieldErrors
                errors={getFieldErrors("applyDiscount", errors) as string[]}
              />
            </CCol>
            {applyDiscount ? (
              <>
                <CCol md={2}>
                  <FormLabel>Descuento Global:</FormLabel>
                </CCol>
                <CCol md={2}>
                  <CurrencyField
                    currency={currency}
                    placeholder="Introduzca el monto"
                    onChange={onGlobalDiscountChange}
                    value={globalDiscount}
                    limit={1000000000000}
                  />
                  <FieldErrors
                    errors={getFieldErrors("unitAmount", errors) as string[]}
                  />
                </CCol>
              </>
            ) : (
              <></>
            )}
          </CRow>
        </CFormGroup>
      </div>
    </>
  );
};

export default AutoInvoiceFormCurrencySection;
